// src/features/calls/providers/microsoftTeamsAdapter.ts
// Microsoft Teams provider adapter for ChatHub call orchestration.
//
// Creates an Outlook Calendar event with isOnlineMeeting=true to get a
// provider-backed Teams meeting link. Falls back to URL-based resolution
// (workspace default / meet.new) when the provider-backed path fails.

import type { ProviderAdapterResult } from '../callOrchestrationService';
import type { AsyncResult, CalendarConnectionError } from '../../calendar/integrations/types';
import { MicrosoftCalendarAuthService } from '../../calendar/integrations/MicrosoftCalendarAuthService';
import { resolveGoogleMeetJoinUrl } from './googleMeetProvider';
import { isDemoMode } from '../../../lib/supabase';
import { CALENDAR_SERVER_INTEGRATION_AVAILABLE } from '../../calendar/integrations/calendarSecurityPolicy';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const GRAPH_API = 'https://graph.microsoft.com/v1.0';
const LOG_TAG = '[microsoftTeamsAdapter]';

// ─────────────────────────────────────────────────────────────────────────────
// Microsoft Graph API Types (subset)
// ─────────────────────────────────────────────────────────────────────────────

/** Outlook event response from Microsoft Graph. */
interface OutlookEventResponse {
    id: string;
    webLink?: string;
    lastModifiedDateTime?: string;
    onlineMeeting?: {
        joinUrl: string;
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapter Configuration
// ─────────────────────────────────────────────────────────────────────────────

export interface MicrosoftTeamsAdapterConfig {
    /** Workspace-configured default meet URL (may be null) */
    defaultMeetUrl?: string | null;
    /** Room the call belongs to (used for event naming) */
    roomId: string;
    /** Call intent — audio or video */
    intent: 'call' | 'video';
    /** Optional agenda for planned calls */
    agenda?: string;
    /** Optional scheduled start time (ISO 8601) for planned calls */
    scheduledStartAt?: string | null;
    /** Optional duration in minutes for planned calls */
    scheduledDurationMinutes?: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a URL-based fallback result (meet.new or workspace default).
 */
function buildFallbackResult(defaultMeetUrl?: string | null): ProviderAdapterResult {
    const joinUrl = resolveGoogleMeetJoinUrl({ defaultMeetUrl });
    return {
        joinUrl,
        externalMeetingId: null,
        calendarEventId: null,
        providerStatus: 'fallback',
        providerMetadata: null,
    };
}

/**
 * Format a Date to local ISO string without Z suffix.
 * Microsoft Graph expects this format alongside an explicit timeZone field.
 */
function formatLocalDateTime(date: Date): string {
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${mo}-${d}T${h}:${mi}:${s}`;
}

/**
 * Build start/end times for the Outlook event.
 * Instant calls: now → now + 1 hour.
 * Planned calls: scheduledStartAt → scheduledStartAt + duration.
 */
function buildEventTimes(
    scheduledStartAt?: string | null,
    scheduledDurationMinutes?: number | null,
): { start: string; end: string } {
    const startDate = scheduledStartAt ? new Date(scheduledStartAt) : new Date();
    const durationMs = (scheduledDurationMinutes ?? 60) * 60 * 1000;
    const endDate = new Date(startDate.getTime() + durationMs);
    return {
        start: formatLocalDateTime(startDate),
        end: formatLocalDateTime(endDate),
    };
}

/**
 * Escape HTML special characters (for event body).
 */
function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

// ─────────────────────────────────────────────────────────────────────────────
// Core: Create Outlook Event with Teams Meeting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates an Outlook Calendar event with `isOnlineMeeting: true`
 * so Microsoft Graph auto-generates a Teams meeting link.
 */
async function createOutlookEventWithTeams(
    accessToken: string,
    config: MicrosoftTeamsAdapterConfig,
): Promise<AsyncResult<OutlookEventResponse>> {
    const { roomId, intent, agenda, scheduledStartAt, scheduledDurationMinutes } = config;
    const times = buildEventTimes(scheduledStartAt, scheduledDurationMinutes);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const intentLabel = intent === 'video' ? 'Video Call' : 'Call';
    const subject = agenda
        ? `📞 AX1 ${intentLabel}: ${agenda.slice(0, 80)}`
        : `📞 AX1 ${intentLabel} (Room ${roomId})`;

    const bodyParts = [
        agenda ? `<p>${escapeHtml(agenda)}</p>` : '',
        '<hr/>',
        '<p><strong>📌 Created via AX1 | Capital ChatHub</strong></p>',
        '<ul>',
        `<li>Room: ${escapeHtml(roomId)}</li>`,
        `<li>Intent: ${intent}</li>`,
        '</ul>',
        '<p><em>⚠️ This meeting is managed by AX1. Edits here will not sync back.</em></p>',
    ];

    const eventBody = {
        subject,
        body: {
            contentType: 'html' as const,
            content: bodyParts.join('\n'),
        },
        start: { dateTime: times.start, timeZone },
        end: { dateTime: times.end, timeZone },
        isOnlineMeeting: true,
        onlineMeetingProvider: 'teamsForBusiness' as const,
        singleValueExtendedProperties: [
            {
                id: 'String {66f5a359-4659-4830-9070-00047ec6ac6e} Name ax1Source',
                value: 'chathub_call',
            },
            {
                id: 'String {66f5a359-4659-4830-9070-00047ec6ac6e} Name ax1RoomId',
                value: roomId,
            },
        ],
    };

    try {
        const response = await fetch(`${GRAPH_API}/me/events`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventBody),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const msg = (errorData as { error?: { message?: string } }).error?.message
                ?? `HTTP ${response.status}`;
            return {
                success: false,
                error: {
                    code: response.status === 429 ? 'RATE_LIMITED' : 'API_ERROR',
                    message: `Microsoft Graph API error: ${msg}`,
                    details: JSON.stringify(errorData),
                    recoverable: true,
                    occurredAt: new Date().toISOString(),
                } satisfies CalendarConnectionError,
            };
        }

        const created: OutlookEventResponse = await response.json();
        return { success: true, data: created };
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return {
            success: false,
            error: {
                code: 'NETWORK_ERROR',
                message: `Failed to create Outlook event: ${message}`,
                recoverable: true,
                occurredAt: new Date().toISOString(),
            } satisfies CalendarConnectionError,
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public Adapter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Microsoft Teams provider adapter.
 *
 * Creates an Outlook Calendar event with `isOnlineMeeting: true` so that
 * Microsoft Graph auto-generates a Teams meeting link. Falls back to
 * URL-based resolution (workspace default / meet.new) when:
 *   - Microsoft Calendar is not connected
 *   - Token retrieval fails
 *   - Graph API call fails
 *   - Response does not contain a Teams join URL
 *
 * The fallback path preserves existing behavior exactly.
 */
export async function microsoftTeamsAdapter(
    config: MicrosoftTeamsAdapterConfig,
): Promise<ProviderAdapterResult> {
    // Demo never claims an external provider event was created. A verified
    // participant URL can still flow through the URL-only fallback adapter.
    if (isDemoMode) {
        return {
            ...buildFallbackResult(config.defaultMeetUrl),
            providerMetadata: { demo: true, provider: 'microsoft_teams', externalEventCreated: false },
        };
    }

    if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) {
        return buildFallbackResult(config.defaultMeetUrl);
    }

    // ── Check connection ─────────────────────────────────────────────────
    const authService = MicrosoftCalendarAuthService.getInstance();
    if (!authService.isConnected()) {
        console.info(LOG_TAG, 'Microsoft Calendar not connected — using fallback');
        return buildFallbackResult(config.defaultMeetUrl);
    }

    // ── Get access token ─────────────────────────────────────────────────
    const tokenResult = await authService.getValidAccessToken();
    if (!tokenResult.success) {
        console.warn(LOG_TAG, 'Token retrieval failed — using fallback', tokenResult.error.message);
        return buildFallbackResult(config.defaultMeetUrl);
    }
    const accessToken = tokenResult.data;

    // ── Create Outlook event with Teams meeting ──────────────────────────
    const eventResult = await createOutlookEventWithTeams(accessToken, config);
    if (!eventResult.success) {
        console.warn(LOG_TAG, 'Event creation failed — using fallback', eventResult.error.message);
        return buildFallbackResult(config.defaultMeetUrl);
    }
    const event = eventResult.data;

    // ── Extract Teams join URL ───────────────────────────────────────────
    const teamsJoinUrl = event.onlineMeeting?.joinUrl;
    if (!teamsJoinUrl) {
        console.warn(LOG_TAG, 'No Teams join URL in response — using fallback');
        // Event was created but has no Teams link — still return the event ID
        const fallbackUrl = resolveGoogleMeetJoinUrl({ defaultMeetUrl: config.defaultMeetUrl });
        return {
            joinUrl: fallbackUrl,
            externalMeetingId: null,
            calendarEventId: event.id,
            providerStatus: 'error',
            providerMetadata: {
                outlookEventId: event.id,
                webLink: event.webLink ?? null,
                fallbackReason: 'no_teams_join_url',
            },
        };
    }

    // ── Success: provider-backed Teams link ──────────────────────────────
    return {
        joinUrl: teamsJoinUrl,
        externalMeetingId: null, // Graph /me/events does not return a separate conferenceId
        calendarEventId: event.id,
        providerStatus: 'created',
        providerMetadata: {
            outlookEventId: event.id,
            webLink: event.webLink ?? null,
        },
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Update / Cancel operations
// ─────────────────────────────────────────────────────────────────────────────

/** Metadata returned after a successful provider event update. */
export interface UpdatedTeamsMeetingMetadata {
    /** The Outlook calendar event ID */
    calendarEventId: string;
    /** Join URL — may change if provider regenerated the meeting link */
    joinUrl: string | null;
}

/** Fields that can be patched on an existing Outlook Calendar event. */
export interface OutlookEventPatch {
    startTime?: string;
    endTime?: string;
    subject?: string;
    bodyHtml?: string;
    /** Email addresses to set as attendees */
    attendees?: string[];
}

/**
 * Updates an existing Outlook Calendar event via Microsoft Graph.
 * Only sends the fields included in `patch`.
 * Returns updated meeting metadata on success, null on failure (logged, non-throwing).
 */
export async function updateOutlookEvent(
    calendarEventId: string,
    patch: OutlookEventPatch,
): Promise<UpdatedTeamsMeetingMetadata | null> {
    if (isDemoMode) {
        console.info(LOG_TAG, 'Demo mode — skipping Outlook event update');
        return { calendarEventId, joinUrl: null };
    }

    const authService = MicrosoftCalendarAuthService.getInstance();
    if (!authService.isConnected()) return null;

    const tokenResult = await authService.getValidAccessToken();
    if (!tokenResult.success) return null;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const body: Record<string, unknown> = {};
    if (patch.startTime) body.start = { dateTime: formatLocalDateTime(new Date(patch.startTime)), timeZone };
    if (patch.endTime) body.end = { dateTime: formatLocalDateTime(new Date(patch.endTime)), timeZone };
    if (patch.subject !== undefined) body.subject = patch.subject;
    if (patch.bodyHtml !== undefined) {
        body.body = { contentType: 'html', content: patch.bodyHtml };
    }
    if (patch.attendees) {
        body.attendees = patch.attendees.map((email) => ({
            emailAddress: { address: email },
            type: 'required' as const,
        }));
    }

    try {
        const response = await fetch(`${GRAPH_API}/me/events/${encodeURIComponent(calendarEventId)}`, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${tokenResult.data}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            console.warn(LOG_TAG, `Event update failed: HTTP ${response.status}`);
            return null;
        }

        const event: OutlookEventResponse = await response.json();
        const joinUrl = event.onlineMeeting?.joinUrl ?? null;
        return { calendarEventId: event.id || calendarEventId, joinUrl };
    } catch (err) {
        console.warn(LOG_TAG, 'Event update error:', err);
        return null;
    }
}

/** Result returned by a provider cancellation operation. */
export interface TeamsMeetingCancellationResult {
    /** Whether the provider-side deletion succeeded */
    cancelled: boolean;
    /** The external event ID that was cancelled */
    calendarEventId: string;
    /** Metadata patch to apply to the local calendar event */
    metadataPatch: {
        meetingProvider: null;
        meetingJoinUrl: null;
        meetingExternalEventId: null;
        meetingSyncStatus: 'synced';
    };
}

/**
 * Cancels (deletes) an Outlook Calendar event via Microsoft Graph.
 * Returns a cancellation result with confirmation and metadata patch,
 * or null on failure (logged, non-throwing).
 * Does NOT delete the local calendar event.
 */
export async function cancelOutlookEvent(
    calendarEventId: string,
): Promise<TeamsMeetingCancellationResult | null> {
    const metadataPatch = {
        meetingProvider: null as null,
        meetingJoinUrl: null as null,
        meetingExternalEventId: null as null,
        meetingSyncStatus: 'synced' as const,
    };

    if (isDemoMode) {
        console.info(LOG_TAG, 'Demo mode — skipping Outlook event cancel');
        return { cancelled: true, calendarEventId, metadataPatch };
    }

    const authService = MicrosoftCalendarAuthService.getInstance();
    if (!authService.isConnected()) return null;

    const tokenResult = await authService.getValidAccessToken();
    if (!tokenResult.success) return null;

    try {
        const response = await fetch(`${GRAPH_API}/me/events/${encodeURIComponent(calendarEventId)}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${tokenResult.data}` },
        });
        // 204 = success, 404 = already deleted (also OK)
        if (!response.ok && response.status !== 404) {
            console.warn(LOG_TAG, `Event cancel failed: HTTP ${response.status}`);
            return null;
        }
        return { cancelled: true, calendarEventId, metadataPatch };
    } catch (err) {
        console.warn(LOG_TAG, 'Event cancel error:', err);
        return null;
    }
}
